/* eslint-disable no-underscore-dangle */
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isFunctionLazyLoadable } from '../../Utils/utils';
import logToConsole from '../../Utils/logToConsole';
import { loadRemoteAnimationJsonFiles } from './lazyLoadable/loadRemoteAnimationJsonFiles';

const LottieLoader = () => {
  const dispatch = useDispatch();
  const enterPhase = useSelector((store) => store.transition.enterPhase);
  const currentPage = useSelector((store) => store.navigation.currentPage);
  const loaderConfig = useSelector(
    (store) => store.lottieAnimationData.loaderConfig
  );

  const [loadLottieFilesRenderer, setLoadLottieFilesRenderer] = useState(false);
  const [animationJsonFileLoaded, setAnimationJsonFileLoaded] = useState(false);

  const lottieAnimationRendererUrl =
    'https://unpkg.com/@lottiefiles/lottie-player@1.6.0/dist/lottie-player.js';

  useEffect(() => {
    /* Note: To appease Core Web Vitals, load lottie dependencies after user interactions
     * Load the first of two lottie dependencies after the enterPhase for reaching page 2 completes
     * Splitting up the loading of lottie resources minimises the chance for a UI lag during the chunk load
     */
    if (
      typeof currentPage === 'number' &&
      enterPhase === false &&
      currentPage > 0 &&
      !animationJsonFileLoaded &&
      !loadLottieFilesRenderer
    ) {
      /**
       * Preload the remote animation JSON files to reduce later latency and race conditions.
       * loadRemoteAnimationJsonFiles is statically imported so it is always part of the initial bundle,
       * making this call reliable regardless of network conditions.
       */
      loadRemoteAnimationJsonFiles(loaderConfig, dispatch);
      setAnimationJsonFileLoaded(true);
    }
  }, [enterPhase]);

  useEffect(() => {
    /* Load the second of two lottie dependencies once the first lottie dependency is loaded
     * This leverages the 400ms-time window of the Transition animation during which the UI is unresponsive anyway
     */
    if (
      typeof currentPage === 'number' &&
      animationJsonFileLoaded &&
      !loadLottieFilesRenderer
    ) {
      setLoadLottieFilesRenderer(true);
    }
  }, [animationJsonFileLoaded]);

  return loadLottieFilesRenderer ? (
    <Script
      src={lottieAnimationRendererUrl}
      onLoad={() => {
        import(
          './lazyLoadable/dispatchAnimationData' /* webpackChunkName: 'dispatchAnimationData' */
        ).then((animationDispatchModule) => {
          if (
            isFunctionLazyLoadable({
              functionName: 'dispatchAnimationData',
              module: animationDispatchModule,
            })
          ) {
            animationDispatchModule.dispatchAnimationData({
              dispatch,
              payload: {
                isRendererAvailable: true,
              },
            });
          }
        });
      }}
      onError={() => {
        logToConsole.warn('[LOTTIE] Failed to fetch optional dependency');
        // Employ re-try strategy via setInterval (every 2 seconds until the request succeeds. Once it does, cleanup the interval)
        let pollingId = setInterval(() => {
          import(
            './lazyLoadable/dispatchAnimationData' /* webpackChunkName: 'dispatchAnimationData' */
          )
            .then((animationDispatchModule) => {
              clearInterval(pollingId); // Prevent this code from running successfully more than once
              pollingId = null; // Release pollingId from the variable
              if (
                isFunctionLazyLoadable({
                  functionName: 'dispatchAnimationData',
                  module: animationDispatchModule,
                })
              ) {
                animationDispatchModule.dispatchAnimationData({
                  dispatch,
                  payload: {
                    isRendererAvailable: true,
                  },
                });
              }
            })
            .catch((error) => {
              // Do nothing and try again in 2 seconds
              logToConsole.warn(
                '[LOTTIE] dispatchAnimationData failed to load:',
                error,
                '/nRe-trying shortly ...'
              );
            });
        }, 2000);
      }}
    />
  ) : null;
};

export default LottieLoader;
